import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AuthTokens {
    accessToken: string | null;
    refreshToken: string | null;
}

@Injectable({
    providedIn: 'root',
})
export class TokenStorageService {
    private readonly ACCESS_TOKEN_KEY = 'accessToken';
    private readonly REFRESH_TOKEN_KEY = 'refreshToken';
    private readonly REMEMBER_ME_KEY = 'rememberMe';
    private readonly REMEMBERED_EMAIL_KEY = 'rememberedEmail';

    private tokensSubject = new BehaviorSubject<AuthTokens>({
        accessToken: this.getAccessToken(),
        refreshToken: this.getRefreshToken(),
    });

    tokens$: Observable<AuthTokens> = this.tokensSubject.asObservable();

    constructor() {
        this.loadTokensFromStorage();
    }

    private getStorage(): Storage {
        // Check if user has rememberMe enabled (check both storages)
        const rememberMe = localStorage.getItem(this.REMEMBER_ME_KEY) === 'true' ||
                          sessionStorage.getItem(this.REMEMBER_ME_KEY) === 'true';
        return rememberMe ? localStorage : sessionStorage;
    }

    getAccessToken(): string | null {
        // Check both storages for backward compatibility
        return localStorage.getItem(this.ACCESS_TOKEN_KEY) || sessionStorage.getItem(this.ACCESS_TOKEN_KEY);
    }

    getRefreshToken(): string | null {
        // Check both storages for backward compatibility
        return localStorage.getItem(this.REFRESH_TOKEN_KEY) || sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
    }

    saveTokens(
        accessToken: string | null,
        refreshToken: string | null,
        rememberMe = localStorage.getItem(this.REMEMBER_ME_KEY) === 'true'
            || sessionStorage.getItem(this.REMEMBER_ME_KEY) === 'true',
    ): void {
        // Rotation preserves the login preference and emits only the replacement
        // tokens, never a transient logged-out state.
        this.clearStoredTokens();

        const storage = rememberMe ? localStorage : sessionStorage;

        if (accessToken) {
            storage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
        }

        if (refreshToken) {
            storage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
        }

        // Store rememberMe preference
        storage.setItem(this.REMEMBER_ME_KEY, String(rememberMe));

        this.tokensSubject.next({
            accessToken,
            refreshToken,
        });
    }

    clearTokens(): void {
        this.clearStoredTokens();
        this.tokensSubject.next({ accessToken: null, refreshToken: null });
    }

    private clearStoredTokens(): void {
        // Clear from both storages
        localStorage.removeItem(this.ACCESS_TOKEN_KEY);
        localStorage.removeItem(this.REFRESH_TOKEN_KEY);
        localStorage.removeItem(this.REMEMBER_ME_KEY);
        sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(this.REMEMBER_ME_KEY);
        // Don't clear remembered email - it should persist for auto-fill

    }

    saveRememberedEmail(email: string): void {
        localStorage.setItem(this.REMEMBERED_EMAIL_KEY, email);
    }

    getRememberedEmail(): string | null {
        return localStorage.getItem(this.REMEMBERED_EMAIL_KEY);
    }

    clearRememberedEmail(): void {
        localStorage.removeItem(this.REMEMBERED_EMAIL_KEY);
    }

    private loadTokensFromStorage(): void {
        const accessToken = this.getAccessToken();
        const refreshToken = this.getRefreshToken();

        this.tokensSubject.next({
            accessToken,
            refreshToken,
        });
    }
}
